import { VOStepper, VOStepperSteps } from "../model/DataStructure.js";
import { Language } from "../model/Language.js";
import { Config } from "../model/Config.js";

export type StepperPanel = "start" | "step" | "end";
export interface StepperPanelLookup {
    [panel: string]: HTMLDivElement;
}

export class Stepper {
    public element: HTMLDivElement;

    private _onProgressFilled = e5.Signal.create();
    private _onProgress = e5.Signal.create<[number]>();
    private _progressBar: HTMLDivElement;
    private _progressThumb: HTMLDivElement;
    private _progress: number = 0;

    private _panelStart: HTMLDivElement;
    private _panelStep: HTMLDivElement;
    private _panelEnd: HTMLDivElement;
    private _panels: StepperPanelLookup = {};

    private _panel: StepperPanel = null;
    private _stepTimeout: any = 0;
    private _stepNextInterval: any = 0;
    private _stepperEntries: HTMLDivElement[] = [];
    private _stepperIndex: number = -1;

    private _iconData: VOStepperSteps[];

    private _maxIdleTime: number;
    private _contentDuration: number;
    private _endAnimationTime: number;
    private _maxWatt: number;
    private _impulsToWatt: number = 1;

    private _wattDe: HTMLSpanElement;
    private _wattEn: HTMLSpanElement;

    constructor(stepper: VOStepper) {
        if (!stepper) {
            console.warn("Stepper could not be instantiated. Missing stepper parameter.");
            return;
        }

        this._contentDuration = Config.contentDurationStepper * 1000;
        this._maxIdleTime = Config.maxIdleTimeStepper * 1000;
        this._endAnimationTime = Config.endScreenDurationStepper * 1000;

        this.element = document.createElement("div");
        this.element.id = "stepper";
        this.element.classList.add("scale-to-fit", "vertical", "green-bg");
        document.body.appendChild(this.element);

        this._panelStart = this.createPanel("start");
        this._panelStep = this.createPanel("step");
        this._panelEnd = this.createPanel("end");

        var logo = document.createElement("div");
        logo.id = "stepper-logo";
        this.element.appendChild(logo);

        this._impulsToWatt = Config.impulsToWatt;

        //Start
        this.addText(
            this._panelStart,
            stepper.title_de,
            stepper.description_de);

        this.addText(
            this._panelStart,
            stepper.title_en,
            stepper.description_en,
            "bottom");

        var startContainerBottom = this.addContainer(this._panelStart);

        var lightBulb = document.createElement("div");
        lightBulb.classList.add("bulb-start", "icon-9");
        startContainerBottom.appendChild(lightBulb);

        //Step
        var stepperContainer = document.createElement("div");
        stepperContainer.classList.add("stepper-container");
        this._panelStep.appendChild(stepperContainer);

        for (var stepperContent of stepper.contents) {
            var stepperEntry = document.createElement("div");
            stepperEntry.classList.add("stepper-container-entry");
            stepperContainer.appendChild(stepperEntry);

            if (stepperContent.stepper_image) {
                var image = document.createElement("img");
                image.classList.add("image");
                image.src = stepperContent.stepper_image.url;
                stepperEntry.appendChild(image);
            }

            this.addText(
                stepperEntry,
                stepperContent.stepper_text_de,
                stepperContent.stepper_text_en);


            this._stepperEntries.push(stepperEntry);
        }

        var stepContainerBottom = this.addContainer(this._panelStep);

        this._progressBar = document.createElement("div");
        this._progressBar.id = "progress-bar";
        this._progressThumb = document.createElement("div");
        this._progressThumb.id = "progress-thumb";
        this._progressBar.appendChild(this._progressThumb);
        stepContainerBottom.appendChild(this._progressBar);

        this._iconData = stepper.steps;

        this._maxWatt = stepper.steps[stepper.steps.length - 1].stepper_watt + Config.wattOffset;

        var wattContainer = document.createElement("div");
        wattContainer.id = "watt-container";
        this._progressBar.appendChild(wattContainer);

        var wattLineDe = document.createElement("div");
        wattLineDe.id = "watt-line-de";
        wattContainer.appendChild(wattLineDe);

        var wattTitleDe = document.createElement("span");
        wattTitleDe.id = "watt-title-en";
        wattTitleDe.innerHTML = Language.translate("stepper.power", "de", "Ihre Energieleistung:");
        wattLineDe.appendChild(wattTitleDe);

        this._wattDe = document.createElement("span");
        this._wattDe.id = "watt-de";
        wattLineDe.appendChild(this._wattDe);

        var wattLineEn = document.createElement("div");
        wattLineEn.id = "watt-line-en";
        wattContainer.appendChild(wattLineEn);

        var wattTitleEn = document.createElement("span");
        wattTitleEn.id = "watt-title-en";
        wattTitleEn.innerHTML = Language.translate("stepper.power", "en", "Your energy performance:");
        wattLineEn.appendChild(wattTitleEn);

        this._wattEn = document.createElement("span");
        this._wattEn.id = "watt-en";
        wattLineEn.appendChild(this._wattEn);

        for (var step of stepper.steps) {
            var stepItem = document.createElement("div");
            stepItem.classList.add("step-item");
            this._progressBar.appendChild(stepItem);

            var stepIcon = document.createElement("div");
            stepIcon.classList.add("step-icon", "icon-" + step.stepper_icon);
            stepItem.appendChild(stepIcon);

            if (step.stepper_text_de) {
                var stepTextDe = document.createElement("div");
                stepTextDe.classList.add("step-text-de");
                stepTextDe.innerHTML = step.stepper_text_de;
                stepItem.appendChild(stepTextDe);
            }

            if (step.stepper_text_en) {
                var stepTextEn = document.createElement("div");
                stepTextEn.classList.add("step-text-en");
                stepTextEn.innerHTML = step.stepper_text_en;
                stepItem.appendChild(stepTextEn);
            }

            step.element = stepItem;
        }

        var wattInfoContainer = document.createElement("div");
        wattInfoContainer.classList.add("step-watt-info-container");
        this._panelStep.appendChild(wattInfoContainer);

        var wattInfoDe = document.createElement("div");
        wattInfoDe.classList.add("step-watt-info", "de");
        wattInfoDe.innerHTML = Language.translate("stepper.watt-info", "de", "Die physikalisch korrekte Bezeichnung lautet Wattsekunden (Ws), zum besseren Verständnis werden hier aber Watt angegeben.");
        wattInfoContainer.appendChild(wattInfoDe);

        var wattInfoEn = document.createElement("div");
        wattInfoEn.classList.add("step-watt-info", "en");
        wattInfoEn.innerHTML = Language.translate("stepper.watt-info", "en", "The physically correct term is joules or watt seconds (Ws), but watts are given here for better understanding.");
        wattInfoContainer.appendChild(wattInfoEn);

        //End

        var endContainerBottom = this.addContainer(this._panelEnd);

        var lightBulb = document.createElement("div");
        lightBulb.classList.add("light-bulb", "icon-9");
        this._panelEnd.appendChild(lightBulb);

        this.addText(
            this._panelEnd,
            Language.translate("stepper.thanks", "de", "Vielen Dank fürs Mitmachen!"),
            Language.translate("stepper.thanks", "en", "Thank you for participating!"));

        var finalImage = document.createElement("div");
        finalImage.classList.add("image");
        if (stepper.final_image) {
            finalImage.style.backgroundImage = "url(" + stepper.final_image.url + ")";
        }
        endContainerBottom.appendChild(finalImage);

        //Set initial panel
        this.panel = "start";

        this._onProgressFilled.add(() => {
            this.panel = "end";
        });

        this._onProgress.add((progress) => {
            wattContainer.classList.toggle("active", progress > 0);

            var watt = this._maxWatt * progress;

            var lastActive: VOStepperSteps = null;
            for (var icon of this._iconData) {
                icon.element.classList.remove("active");
                if (icon.stepper_watt <= watt)
                    lastActive = icon;
            }

            var wattText = watt.toFixed(Config.fractionDigitsStepper) + " Watt";
            if (watt > 1000) {
                wattText = (watt / 1000).toFixed(2) + " Kilowatt";
            }
            this._wattDe.innerHTML = wattText;
            this._wattEn.innerHTML = wattText;

            if (lastActive) {
                lastActive.element.classList.add("active");
            }
        });

        // gs.TweenMax.to(this, 5, { progress: 1, ease: gs.Linear.easeNone });
        window.addEventListener("keydown", (e) => {
            if (e.keyCode == 106) {
                this.impulse();
            }
        });

        window.addEventListener("pointerdown", (e) => {
            this.impulse();
        });
    }

    private impulse(): void {
        if (this._panel == "start") {
            this.panel = "step";
            return;
        }
        var watt = this._impulsToWatt;
        this.progress += watt / this._maxWatt;
    }

    private addContainer(panel: HTMLDivElement): HTMLDivElement {
        var container = document.createElement("div");
        container.classList.add("container-bottom");
        panel.appendChild(container);
        return container;
    }

    private addText(container: HTMLDivElement, text_de: string, text_en: string, custom_class: string = ""): void {
        var title = document.createElement("div");
        title.classList.add("title");
        if (custom_class)
            title.classList.add(custom_class);
        title.innerHTML = text_de;
        container.appendChild(title);

        var text = document.createElement("div");
        text.classList.add("text");
        if (custom_class)
            text.classList.add(custom_class);
        text.innerHTML = text_en;
        container.appendChild(text);
    }

    private createPanel(id: StepperPanel): HTMLDivElement {
        var panel = document.createElement("div");
        panel.id = id;
        panel.classList.add("panel");
        this.element.appendChild(panel);
        this._panels[id] = panel;
        return panel;
    }

    private get stepperIndex(): number {
        return this._stepperIndex;
    }

    private set stepperIndex(index: number) {
        index = e5.Calc.clamp(index, -1, this._stepperEntries.length - 1);
        if (this._stepperIndex == index)
            return;

        if (this._stepperIndex >= 0) {
            this._stepperEntries[this._stepperIndex].classList.remove("active");
        }
        this._stepperIndex = index;
        if (this._stepperIndex >= 0) {
            this._stepperEntries[this._stepperIndex].classList.add("active");
        }
    }

    private stepNext(): void {
        if (this.stepperIndex == this._stepperEntries.length - 1)
            this.stepperIndex = 0;
        else
            this.stepperIndex++;
    }

    private get progress(): number {
        return this._progress;
    }

    private set progress(progress: number) {
        progress = e5.Calc.clamp(progress, 0, 1);
        if (this._progress == progress)
            return;
        this._progress = progress;
        this._progressThumb.style.width = (progress * 100) + "%";
        if (progress == 1) {
            this._onProgressFilled.dispatch();
        }

        this._onProgress.dispatch(progress);

        clearTimeout(this._stepTimeout);
        this._stepTimeout = setTimeout(() => {
            if (this._panel == "step")
                this.panel = "start";
        }, this._maxIdleTime);
    }

    private get panel(): StepperPanel {
        return this._panel;
    }

    private set panel(panel: StepperPanel) {
        if (this._panel == panel)
            return;

        if (this._panel) {
            this._panels[this._panel].classList.remove("active");
        }
        this._panel = panel;

        clearTimeout(this._stepTimeout);
        clearInterval(this._stepNextInterval);

        if (this._panel) {
            if (this._panel == "start") {
                this.progress = 0;
            }
            else if (this._panel == "step") {
                this.stepperIndex = 0;
                this._stepNextInterval = setInterval(() => {
                    this.stepNext();
                }, this._contentDuration);
                this.progress = 0;
            }
            else if (this._panel == "end") {
                setTimeout(() => {
                    this.panel = "start";
                }, this._endAnimationTime);
            }
            this._panels[this._panel].classList.add("active");
        }
    }
}